import { t } from '@lingui/core/macro';
import {
  oauthService,
  OAUTH_PROVIDERS,
  AgenticProcess,
  dataContext,
  snifferManager,
  tabManager,
} from '@sdk';
import { gitResolvePrompt } from '@src/lib/git-resolve-prompt';
import { notify } from './notify';
import { settleAsk } from './pending-asks';
import type { NotificationAction } from './types';
import { commandFor, registerCommand, type CommandArgs } from './command-registry';

/**
 * Imperative command registry for notification actions. A `NotificationAction`
 * carries a serializable `command` key (not a callback) so backend-originated
 * notifications work too; the actual function is looked up here at click time.
 *
 * Navigation actions use `action.href` instead (URL-first) — see `navigateTo`.
 */
export { registerCommand, type CommandArgs } from './command-registry';

// The answer to an `askNotification` button. Built in here so it exists as soon as commands.ts loads.
registerCommand('notification.answer', (args, ctx) => {
  settleAsk(ctx.id, { value: String(args.value), remember: args.remember === true });
  notify.dismiss(ctx.id);
});

export function runCommand(name: string, args: CommandArgs, ctx: { id: string }): void {
  const fn = commandFor(name);
  if (fn) fn(args, ctx);
  else console.warn(`[notify] unknown command '${name}'`);
}

/** Navigation handle, registered URL-first by the command-bridge (react-router). */
let navHandle: ((href: string) => void) | null = null;
export function registerNavigate(fn: (href: string) => void): void {
  navHandle = fn;
}
export function navigateTo(href: string): void {
  if (navHandle) navHandle(href);
  else window.location.assign(href);
}

/** Run a notification action: imperative `command`, else URL-first `href`. `extra` is merged into
 *  the command's args — what the toast itself knows at click time (the `remember` box). */
export function runAction(action: NotificationAction, id: string, extra?: CommandArgs): void {
  if (action.command) runCommand(action.command, { ...action.args, ...extra }, { id });
  else if (action.href) navigateTo(action.href);
}

// --- Static commands (module-level deps; no React hooks) ---------------------

// `Sign in` on the expired-cloud-sign-in toast: relaunch the cloud OAuth flow.
registerCommand('cloud.signin', () => {
  void oauthService.connect(OAUTH_PROVIDERS.FLOWPAD_CLOUD);
});

registerCommand('terminal.terminate', (args) => {
  if (args.typeId) void tabManager.closeTarget(String(args.typeId));
});

// `Resolve` — THE way out of a git conflict, from any surface that hit one
// (built by `resolveConflictAction` in lib/git-outcome.ts). Launches an agentic
// process seeded with the conflict-resolution prompt for the branch
// (`origin: 'pull'` finishes without pushing) in the repo that conflicted:
// `workdir`, keeping the current project when it is that repo (a task repo or a
// context folder is not); absent → the current project.
registerCommand('git.resolve-conflict', (args) => {
  const branch = String(args.branch ?? '');
  const origin = args.origin === 'pull' || args.origin === 'cherry-pick' ? args.origin : 'push';
  const workdir = typeof args.workdir === 'string' && args.workdir ? args.workdir : null;
  const where =
    workdir && workdir !== dataContext.project?.fs_storage_mount_path ? { fs_storage_mount_path: workdir } : undefined;
  void AgenticProcess.openTab('claude_code', gitResolvePrompt(branch, origin), where).catch((e: unknown) => {
    notify.error({ title: t`Could not start resolver`, message: String(e) });
  });
});

// `Disable` on the startup "hook sniffer is on" toast: clear the harness hooks
// (whichever instance installed them) and record the opt-out so a boot doesn't
// silently put them back.
registerCommand('sniffer.disable', (_args, ctx) => {
  void snifferManager
    .disable()
    .then(() => {
      notify.dismiss(ctx.id);
      notify.success({
        title: t`Hook sniffer disabled`,
        message: t`Claude Code hooks were removed from your settings.`,
      });
    })
    .catch((e: unknown) => {
      notify.error({ title: t`Could not disable the sniffer`, message: String(e) });
    });
});

registerCommand('notification.dismiss', (_args, ctx) => {
  // One dismiss path for all three surfaces — toast, sidebar badge, alert log.
  notify.dismiss(ctx.id);
});

// `terminal.resume` is hook-bound (useResumeInTerminal) and is registered at
// runtime by <NotificationCommandBridge/>.
